import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${window.location.origin}/api/budget`;
// The Sales view can be the first page opened: carry the session token the way BudgetApp does.
const auth = () => { const t = localStorage.getItem("auth_token"); return t ? { headers: { Authorization: `Bearer ${t}` } } : {}; };

/** BOD-1 — ONE record set (`budgets.selected_scopes[].spec_*`) behind both views. The Budgets dashboard and
 *  Sales › Basis of Design call the same /api/budget/admin/specs endpoints; a normal re-fetch is the only sync. */
export function useBasisOfDesign() {
  const [rows, setRows] = useState([]);
  const [emailRow, setEmailRow] = useState(null);

  const load = useCallback(async () => {
    try { setRows((await axios.get(`${API}/admin/specs`, auth())).data.rows || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load Basis of Design"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const same = (a, b) => a.budget_id === b.budget_id && a.scope_code === b.scope_code;
  const patch = async (row, body) => {
    try {
      const { data } = await axios.patch(`${API}/admin/specs/${row.budget_id}`, { scope_code: row.scope_code, ...body }, auth());
      setRows((rs) => rs.map((r) => (same(r, row) ? { ...r, spec_status: data.spec_status, notes: data.notes } : r)));
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save that"); return false; }
  };
  const addNote = (row, text) => patch(row, { spec_note: text });
  // changes = { architect_contact_name, architect_contact_email } or { gc_contact_name, gc_contact_email }
  const saveContact = async (row, changes) => {
    const apply = () => setRows((rs) => rs.map((r) => (r.budget_id === row.budget_id ? { ...r, ...changes } : r)));
    if (Object.keys(changes)[0].startsWith("gc_")) {   // legacy GC contact (no GC picked yet) — same tracker PATCH
      if (await patch(row, changes)) apply();
      return;
    }
    const next = { ...row, ...changes };
    try {
      await axios.put(`${API}/admin/architect-contact`, {
        budget_id: row.budget_id, company: row.architect, contact_name: next.architect_contact_name,
        contact_email: next.architect_contact_email, contact_phone: next.architect_contact_phone,
      }, auth());
      apply();
      toast.success("Saved to the directory");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the contact"); }
  };
  // One contact per GC — saved on that GC's invitation (bid) / project info (budget), then re-fetched.
  const saveGcContacts = async (row, entries) => {
    for (const c of entries) {
      if (!(await patch(row, { gc_contact: { gc: c.gc, name: c.name, email: c.email } }))) break;
    }
    load();
  };
  const closeEmail = (sent) => { setEmailRow(null); if (sent) load(); };
  // BOD-1 §2 — manual "Log contact": date + note, sets last_contact and appends a stamped note.
  const logContact = async (row) => {
    const date = window.prompt("Contact date (YYYY-MM-DD)", new Date().toISOString().slice(0, 10));
    if (date === null) return;
    const note = window.prompt("Note (who / what was said)", "");
    if (note === null) return;
    try { await axios.post(`${API}/admin/specs/${row.budget_id}/contact`, { scope_code: row.scope_code, date, note }, auth()); toast.success("Contact logged"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not log the contact"); }
  };
  return { rows, load, patch, addNote, saveContact, saveGcContacts, emailRow, setEmailRow, closeEmail, logContact };
}
